// Enhanced LateLoginTable.jsx with:
// - Clickable summary cards (Max Late, Avg Late)
// - Badge filter support in Focus Mode
// - Simplified visual indicator instead of donut chart

import React, { useState, useEffect } from 'react';
import api from '../../../../api';
import { motion } from 'framer-motion';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { FaClock, FaUserCircle } from 'react-icons/fa';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import { useAuth } from '../../../Layouts/context/Authcontext';

const brandColors = {
  red: '#EE1D23',
  blue: '#1A2B5C',
  gray: '#808080',
};

const LateLoginTable = ({ filters }) => {
  const { accessToken } = useAuth();
  const [lateUsers, setLateUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);

  useEffect(() => {
    const fetchLateUsers = async () => {
      try {
        setLoading(true);
        const baseUrl = getRoundRobinBaseUrl();
        const params = {};
        Object.entries(filters || {}).forEach(([key, value]) => {
          if (value) params[key] = value;
        });

        const res = await api.get(`${baseUrl}/superAdminDashboard/getSuperAdminLateLogins`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params,
        });

        if (res.data.success && Array.isArray(res.data.data)) {
          const parsed = res.data.data.map((item) => ({
            name: item.employeeName,
            late: item.NoOfLate,
          }));
          setLateUsers(parsed);
        } else {
          setLateUsers([]);
        }
      } catch (err) {
        console.error('Fetch Error:', err);
        setLateUsers([]);
      } finally {
        setLoading(false);
      }
    };

    if (filters) fetchLateUsers();
  }, [filters]);

  const getBadgeCounts = () => {
    const counts = { RARE: 0, OCCASIONAL: 0, FREQUENT: 0 };
    lateUsers.forEach((u) => {
      if (u.late >= 8) counts.FREQUENT++;
      else if (u.late >= 4) counts.OCCASIONAL++;
      else counts.RARE++;
    });
    return counts;
  };

  const badgeCounts = getBadgeCounts();
  const maxLate = Math.max(...lateUsers.map((u) => u.late), 0);
  const avgLate = lateUsers.length ? Math.round(lateUsers.reduce((a, b) => a + b.late, 0) / lateUsers.length) : 0;

  const filteredUsers = selectedBadge
    ? lateUsers.filter((user) => {
        if (selectedBadge === 'RARE') return user.late < 4;
        if (selectedBadge === 'OCCASIONAL') return user.late >= 4 && user.late < 8;
        if (selectedBadge === 'FREQUENT') return user.late >= 8;
        if (selectedBadge === 'MAX_LATE') return user.late === maxLate;
        if (selectedBadge === 'AVG_LATE') return user.late >= avgLate;
        return true;
      })
    : lateUsers;

  const visibleUsers = showAll ? filteredUsers : filteredUsers.slice(0, 5);

  const Summary = () => (
    <div className="grid grid-cols-3 gap-4 mt-6 text-sm">
      <div
        onClick={() => setSelectedBadge('MAX_LATE')}
        className="cursor-pointer hover:scale-[1.02] transition bg-[#EE1D23] text-white p-4 rounded-xl text-center shadow-md"
      >
        <p>Max Late</p>
        <p className="text-2xl font-bold">{maxLate}</p>
      </div>
      <div
        onClick={() => setSelectedBadge('AVG_LATE')}
        className="cursor-pointer hover:scale-[1.02] transition bg-[#1A2B5C] text-white p-4 rounded-xl text-center shadow-md"
      >
        <p>Avg Late</p>
        <p className="text-2xl font-bold">{avgLate}</p>
      </div>
      <div className="bg-[#1A2B5C] text-white p-4 rounded-xl text-center shadow-md">
        <p>Total Users</p>
        <p className="text-2xl font-bold">{filteredUsers.length}</p>
      </div>
    </div>
  );

  const renderBadgeFilters = () => (
    <div className="flex flex-wrap gap-2 mt-2">
      {['RARE', 'OCCASIONAL', 'FREQUENT'].map((badge) => {
        const isActive = selectedBadge === badge;
        const bg = badge === 'FREQUENT' ? 'bg-[#EE1D23]' : badge === 'OCCASIONAL' ? 'bg-orange-400' : 'bg-gray-300';
        const text = badge === 'RARE' ? 'text-gray-800' : 'text-white';
        const glow = isActive ? 'ring-2 ring-offset-1 ring-[#1A2B5C] shadow-lg' : 'shadow-sm';

        return (
          <span
            key={badge}
            onClick={() => setSelectedBadge(selectedBadge === badge ? null : badge)}
            className={`text-sm font-semibold px-4 py-1.5 rounded-full cursor-pointer transition-all duration-200 ${bg} ${text} ${glow}`}
          >
            {badge} ({badgeCounts[badge]})
          </span>
        );
      })}
      {selectedBadge && (
        <button
          onClick={() => setSelectedBadge(null)}
          className="text-sm underline text-[#1A2B5C] hover:text-[#EE1D23] transition"
        >
          Clear
        </button>
      )}
    </div>
  );
  const getStatusBadge = (late) => {
    const base = 'text-xs font-bold px-2 py-0.5 rounded-full shadow-sm tracking-wide';
    if (late >= 8) return <span className={`${base} bg-[#EE1D23] text-white`}>FREQUENT</span>;
    if (late >= 4) return <span className={`${base} bg-orange-400 text-white`}>OCCASIONAL</span>;
    return <span className={`${base} bg-gray-300 text-gray-800`}>RARE</span>;
  };

  const Table = ({ users, heightClass = 'max-h-[60vh]' }) => (
    <div className={`overflow-y-auto ${heightClass} custom-scroll`}>
      <ul className="divide-y divide-gray-200 text-[15px]">
        <li className="flex justify-between font-semibold text-gray-700 mb-2 px-1">
          <span className="flex-1">Employee</span>
          <span className="w-24 text-right pr-4"># Late</span>
        </li>

        {loading ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-[#1A2B5C]" />
          </div>
        ) : users.length ? (
          users.map((user, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="flex items-center justify-between py-3 px-1 hover:bg-slate-50 transition-all"
            >
              <span className="flex items-center gap-4 flex-1">
                <div className="w-10 h-10 bg-[#1A2B5C] rounded-full text-white flex items-center justify-center shadow-md">
                  <FaUserCircle size={20} />
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-[16px] font-semibold text-[#1A2B5C]">{user.name}</span>
                  {getStatusBadge(user.late)}
                </div>
              </span>
              <span className="w-24 text-right pr-4 text-[#EE1D23] font-semibold text-[15px]">{user.late}</span>
            </motion.li>
          ))
        ) : (
          <li className="text-center text-gray-500 italic py-6">No late logins</li>
        )}
      </ul>
    </div>
  );

  return (
    <>
      <div className="rounded-2xl bg-white shadow-xl p-6 border-t-4" style={{ borderColor: brandColors.red }}>
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-[#EE1D23] p-2 rounded-full text-white">
              <FaClock size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#1A2B5C]">Late Logins</h2>
              {renderBadgeFilters()}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                const worksheet = XLSX.utils.json_to_sheet(lateUsers);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, 'LateLogins');
                const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
                saveAs(blob, 'LateLogins.xlsx');
              }}
              className="bg-gradient-to-r from-[#1A2B5C] to-[#EE1D23] px-4 py-2 text-sm text-white rounded-full font-semibold shadow hover:opacity-90"
            >
              Export
            </button>

            <button
              onClick={() => setFocusMode(true)}
              className="px-4 py-2 text-sm font-semibold border border-[#1A2B5C] rounded-full text-[#1A2B5C] hover:bg-[#1A2B5C] hover:text-white"
            >
              Focus Mode
            </button>
          </div>
        </div>

        <Table users={visibleUsers} />

        {!loading && filteredUsers.length > 5 && (
          <div className="text-center mt-4">
            <button
              onClick={() => setShowAll(!showAll)}
              className="text-sm font-semibold text-[#1A2B5C] underline hover:text-[#EE1D23] transition"
            >
              {showAll ? 'Show Less' : `Show ${filteredUsers.length - 5} More`}
            </button>
          </div>
        )}

        {!loading && filteredUsers.length > 0 && <Summary />}
      </div>

      {focusMode && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-center items-center p-4">
          <motion.div
            className="w-full max-w-6xl bg-white rounded-2xl p-6 relative"
            initial={{ scale: 0.9, y: 60, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 100 }}
          >
            <button
              onClick={() => setFocusMode(false)}
              className="absolute top-4 right-4 bg-white p-2 rounded-full shadow text-gray-700 hover:text-[#EE1D23]"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            <div className="mb-4">
              <h3 className="text-2xl font-bold text-[#1A2B5C]">
                Late Logins – Focus Mode {selectedBadge && `(${selectedBadge})`}
              </h3>
              {renderBadgeFilters()}
            </div>

            <div className="grid grid-cols-3 gap-4 text-center mb-6">
              <div className="p-4 rounded-xl bg-gray-100">
                <p className="text-gray-600 font-medium">Rare</p>
                <p className="text-2xl text-[#1A2B5C] font-bold">{badgeCounts.RARE}</p>
              </div>
              <div className="p-4 rounded-xl bg-gray-100">
                <p className="text-gray-600 font-medium">Occasional</p>
                <p className="text-2xl text-orange-500 font-bold">{badgeCounts.OCCASIONAL}</p>
              </div>
              <div className="p-4 rounded-xl bg-gray-100">
                <p className="text-gray-600 font-medium">Frequent</p>
                <p className="text-2xl text-[#EE1D23] font-bold">{badgeCounts.FREQUENT}</p>
              </div>
            </div>

            <div className="mt-6">
              <Table users={filteredUsers} heightClass="max-h-[65vh]" />
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default LateLoginTable;
